<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <v-row>
                <v-col cols="12" sm="12" md="8">
                    <h1>Generate Transaction by date range</h1>
                </v-col>
                <v-col cols="12" sm="12" md="4"> </v-col>
            </v-row>
            <v-expansion-panels v-model="data.panel" multiple>
                <v-expansion-panel>
                    <v-expansion-panel-title>
                        <h2>Select date range</h2>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <v-row>
                            <date-range-select
                                v-model="data.generateTransactionRequest.dateTransactionOn"
                                :from-label="formLabelText.dateTransactionOnFrom"
                                :to-label="formLabelText.dateTransactionOnTo"
                                :enable-validation="true"
                                @validation-change="onDateRangeValidationChange"
                            />
                        </v-row>
                        <v-row class="mt-2">
                            <v-btn class="mr-2" variant="text" @click="onResetClick">
                                <v-icon>mdi-cached</v-icon>
                                Clear
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn class="mr-2" variant="text" @click="onGenerateClick">
                                <v-icon>mdi-database-sync</v-icon>
                                Generate Transaction
                            </v-btn>
                        </v-row>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
            <alert-message ref="errorAlert" :message="data.errorAlertMessage"></alert-message>
            <v-card>
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="6">
                            <v-btn class="mt-2" variant="text" @click="onListTransactionClick"> Go to list </v-btn>
                        </v-col>
                        <v-col cols="12" sm="12" md="6">
                            <v-spacer></v-spacer>
                        </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <!-- when generate submitted, show processing message -->
                    <div v-if="data.processing" class="processing-section">
                        <v-progress-circular indeterminate color="primary" class="mr-3"></v-progress-circular>
                        <span class="processing-text"> Processing transaction generation... </span>
                    </div>

                    <!-- when generate success, show comprehensive statistics -->
                    <div v-if="data.resultSuccess" class="success-section">
                        <v-alert
                            :type="
                                data.generationResult.status === 'FULL_SUCCESS'
                                    ? 'success'
                                    : data.generationResult.status === 'PARTIAL_SUCCESS'
                                    ? 'warning'
                                    : 'error'
                            "
                            variant="tonal"
                            class="mb-4"
                        >
                            <v-alert-title>
                                <v-chip
                                    :color="
                                        data.generationResult.status === 'FULL_SUCCESS'
                                            ? 'success'
                                            : data.generationResult.status === 'PARTIAL_SUCCESS'
                                            ? 'warning'
                                            : 'error'
                                    "
                                    size="small"
                                    class="mr-2"
                                >
                                    {{ data.generationResult.status }}
                                </v-chip>
                                {{
                                    data.generationResult.status === 'FULL_SUCCESS'
                                        ? 'All transactions generated successfully'
                                        : data.generationResult.status === 'PARTIAL_SUCCESS'
                                        ? 'Transactions partially generated - some errors occurred'
                                        : 'Transaction generation failed'
                                }}
                            </v-alert-title>
                        </v-alert>

                        <!-- Critical Errors Section -->
                        <div
                            v-if="
                                data.generationResult.criticalErrors && data.generationResult.criticalErrors.length > 0
                            "
                            class="critical-errors-section mb-4"
                        >
                            <v-expansion-panels variant="accordion">
                                <v-expansion-panel>
                                    <v-expansion-panel-title class="text-error">
                                        <v-icon class="mr-2">mdi-alert-circle-outline</v-icon>
                                        Critical Errors ({{ data.generationResult.criticalErrors.length }})
                                    </v-expansion-panel-title>
                                    <v-expansion-panel-text>
                                        <v-alert type="error" variant="outlined" class="mb-2">
                                            <div class="text-body-2">
                                                The following critical errors prevented some days from being processed:
                                            </div>
                                        </v-alert>
                                        <div class="critical-errors-list">
                                            <v-card
                                                v-for="(error, index) in data.generationResult.criticalErrors"
                                                :key="index"
                                                variant="outlined"
                                                class="mb-2 pa-3"
                                            >
                                                <div class="d-flex align-start">
                                                    <v-icon color="error" class="mr-2">mdi-alert</v-icon>
                                                    <div class="text-body-2 text-error flex-grow-1">
                                                        {{ error }}
                                                    </div>
                                                </div>
                                            </v-card>
                                        </div>
                                    </v-expansion-panel-text>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </div>

                        <v-row class="mb-3">
                            <v-col cols="12" md="6">
                                <v-card variant="outlined" class="pa-3">
                                    <div class="text-h6 mb-2">Processing Summary</div>
                                    <div class="text-body-1">
                                        <div
                                            v-if="
                                                data.generationResult.daysProcessed &&
                                                data.generationResult.daysProcessed > 0
                                            "
                                            class="mb-2 pa-2"
                                            style="background-color: #f5f5f5; border-radius: 4px"
                                        >
                                            <strong>Days Processed:</strong>
                                            {{ data.generationResult.daysProcessed }}
                                            <span v-if="data.generationResult.daysSucceeded > 0" class="text-success">
                                                ({{ data.generationResult.daysSucceeded }} succeeded<span
                                                    v-if="data.generationResult.daysFailed > 0"
                                                    >,
                                                </span>
                                            </span>
                                            <span v-if="data.generationResult.daysFailed > 0" class="text-error">
                                                {{ data.generationResult.daysFailed }} failed</span
                                            >
                                            <span v-if="data.generationResult.daysSucceeded > 0">)</span>
                                        </div>
                                        <div>
                                            <strong>Total Processed:</strong>
                                            {{ data.generationResult.totalProcessed }}
                                        </div>
                                        <div class="text-success">
                                            <strong>Successful Generations:</strong>
                                            {{ data.generationResult.successCount }}
                                        </div>
                                        <div v-if="data.generationResult.failedCount > 0" class="text-error">
                                            <strong>Failed Generations:</strong>
                                            {{ data.generationResult.failedCount }}
                                        </div>
                                        <div>
                                            <strong>Orders Updated:</strong>
                                            {{ data.generationResult.ordersUpdated }}
                                        </div>
                                        <div>
                                            <strong>Processing Time:</strong>
                                            {{ (data.generationResult.processingTimeMs / 1000).toFixed(2) }}s
                                        </div>
                                    </div>
                                </v-card>
                            </v-col>
                        </v-row>

                        <!-- Show failed transactions if any -->
                        <div
                            v-if="
                                data.generationResult.failedCount > 0 &&
                                data.generationResult.failedTransactions.length > 0
                            "
                            class="failed-transactions-section"
                        >
                            <v-expansion-panels variant="accordion">
                                <v-expansion-panel>
                                    <v-expansion-panel-title class="text-error">
                                        <v-icon class="mr-2">mdi-alert-circle</v-icon>
                                        Failed Transactions ({{ data.generationResult.failedTransactions.length }})
                                    </v-expansion-panel-title>
                                    <v-expansion-panel-text>
                                        <div class="failed-transactions-list">
                                            <div
                                                v-for="failedTransaction in data.generationResult.failedTransactions"
                                                :key="failedTransaction.sourceId"
                                                class="failed-transaction-item"
                                            >
                                                <v-card variant="outlined" class="mb-2">
                                                    <v-card-text class="pa-3">
                                                        <v-row no-gutters>
                                                            <v-col cols="12" sm="6" md="3">
                                                                <div class="text-caption text-grey-darken-1">
                                                                    Order Number
                                                                </div>
                                                                <div class="text-body-2 font-weight-medium">
                                                                    {{ failedTransaction.orderNumber || 'N/A' }}
                                                                </div>
                                                            </v-col>
                                                            <v-col cols="12" sm="6" md="2">
                                                                <div class="text-caption text-grey-darken-1">
                                                                    Source Type
                                                                </div>
                                                                <v-chip size="small" color="primary" variant="outlined">
                                                                    {{ failedTransaction.sourceType }}
                                                                </v-chip>
                                                            </v-col>
                                                            <v-col cols="12" md="4">
                                                                <div class="text-caption text-grey-darken-1">
                                                                    Failure Reason
                                                                </div>
                                                                <div class="text-body-2 text-error">
                                                                    {{ failedTransaction.reason }}
                                                                </div>
                                                            </v-col>
                                                            <v-col cols="12" md="3">
                                                                <div class="text-caption text-grey-darken-1">
                                                                    Source ID
                                                                </div>
                                                                <div class="text-body-2 font-mono text-grey-darken-2">
                                                                    {{ failedTransaction.sourceId }}
                                                                </div>
                                                            </v-col>
                                                        </v-row>
                                                    </v-card-text>
                                                </v-card>
                                            </div>
                                        </div>
                                    </v-expansion-panel-text>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </div>
                    </div>

                    <!-- when generate result error, show error message -->
                    <div v-if="data.resultFail" class="error-section">
                        <v-alert type="error" variant="tonal">
                            <v-alert-title>Generation Failed</v-alert-title>
                            {{ data.errorAlertMessage }}
                        </v-alert>
                    </div>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive } from 'vue';
import * as _ from 'lodash';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { TransactionLabelTextHelper } from '../../utils/labelTextHelper/TransactionLabelTextHelper';
import { Formatter } from '../../utils/Formatter';
import { PathDefinition } from '../../utils/PathDefinition';
import DateRangeSelect from '../../components/DateRangeSelect.vue';
import AlertMessage from '../../components/AlertMessage.vue';
import { TransactionHeaderHelper } from '../../utils/headerHelper/TransactionHeaderHelper';

dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(utc);

export default defineComponent({
    name: 'GenerateTransaction',

    components: {
        AlertMessage,
        DateRangeSelect
    },

    setup() {
        const data = reactive({
            panel: [0],
            processing: false,
            resultSuccess: false,
            resultFail: false,
            errorAlertMessage: '',
            generateTransactionRequestDefault: {},
            generateTransactionRequest: {
                dateTransactionOn: {
                    from: null,
                    to: null
                }
            },
            generationResult: {
                status: '',
                successCount: 0,
                failedCount: 0,
                totalProcessed: 0,
                failedTransactions: [] as any[],
                ordersUpdated: 0,
                processingTimeMs: 0,
                criticalErrors: [] as string[],
                daysProcessed: 0,
                daysSucceeded: 0,
                daysFailed: 0
            },
            validation: {
                hasErrors: false
            }
        });
        return {
            datepickerPreviewFormat: Formatter.defaultDatepickerPreviewFormat,
            formLabelText: TransactionLabelTextHelper.getLabelText,
            actionHeaderItem: TransactionHeaderHelper.getActionHeaderItem(),
            headers: [...TransactionHeaderHelper.getHeaders()],
            data
        };
    },
    computed: {},
    async created() {
        this.data.generateTransactionRequestDefault = _.cloneDeep(this.data.generateTransactionRequest);
    },
    methods: {
        onResetClick() {
            this.data.generateTransactionRequest = _.cloneDeep(this.data.generateTransactionRequestDefault);
            this.data.generationResult = {
                status: '',
                successCount: 0,
                failedCount: 0,
                totalProcessed: 0,
                failedTransactions: [],
                ordersUpdated: 0,
                processingTimeMs: 0,
                criticalErrors: [],
                daysProcessed: 0,
                daysSucceeded: 0,
                daysFailed: 0
            };
            this.data.resultSuccess = false;
            this.data.resultFail = false;
        },
        async onGenerateClick() {
            if (this.data.validation.hasErrors) {
                this.data.errorAlertMessage = 'Please fix the date range errors before generating';
                this.$refs.errorAlert.showAlert();
                return;
            }

            const request = {
                dateTransactionOnFrom: Formatter.convertDateToStandardFormatDateStr(
                    this.data.generateTransactionRequest.dateTransactionOn.from
                ),
                dateTransactionOnTo: Formatter.convertDateToStandardFormatDateStr(
                    this.data.generateTransactionRequest.dateTransactionOn.to
                )
            };
            const requestUrl = `/api/staff/transaction/generateTransactionsByDateRange`;
            this.data.processing = true;
            this.data.resultSuccess = false;
            this.data.resultFail = false;

            try {
                const response = await fetchWrapper.post(requestUrl, request);
                if (response.success) {
                    // Handle the GenerateTransactionResult structure
                    this.data.generationResult = {
                        status: response.result.status || '',
                        successCount: response.result.successCount || 0,
                        failedCount: response.result.failedCount || 0,
                        totalProcessed: response.result.totalProcessed || 0,
                        failedTransactions: response.result.failedTransactions || [],
                        ordersUpdated: response.result.ordersUpdated || 0,
                        processingTimeMs: response.result.processingTimeMs || 0,
                        criticalErrors: response.result.criticalErrors || [],
                        daysProcessed: response.result.daysProcessed || 0,
                        daysSucceeded: response.result.daysSucceeded || 0,
                        daysFailed: response.result.daysFailed || 0
                    };
                    this.data.processing = false;
                    this.data.resultSuccess = true;
                    return response.result;
                } else {
                    this.data.processing = false;
                    this.data.resultFail = true;
                    this.data.errorAlertMessage = response.errors || 'Generation failed';
                    this.$refs.errorAlert.showAlert();
                }
            } catch (error) {
                this.data.processing = false;
                this.data.resultFail = true;
                this.data.errorAlertMessage = 'An unexpected error occurred during generation';
                this.$refs.errorAlert.showAlert();
            }
        },
        onDateRangeValidationChange(validationStatus: { hasErrors: boolean }) {
            this.data.validation.hasErrors = validationStatus.hasErrors;
        },
        async onListTransactionClick(item: any) {
            await this.$router.push({ path: PathDefinition.listTransaction });
        }
    }
});
</script>

<style>
.validation-error {
    color: red;
}

.processing-section {
    display: flex;
    align-items: center;
    padding: 20px;
    text-align: center;
}

.processing-text {
    font-size: 16px;
    color: #1976d2;
}

.success-section {
    margin-top: 16px;
}

.error-section {
    margin-top: 16px;
}

.failed-transactions-section {
    margin-top: 16px;
}

.failed-transactions-list {
    max-height: 400px;
    overflow-y: auto;
}

.failed-transaction-item {
    transition: all 0.2s ease-in-out;
}

.failed-transaction-item:hover {
    transform: translateY(-1px);
}

.font-mono {
    font-family: 'Courier New', Courier, monospace;
    font-size: 0.85em;
}
</style>
